import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../services/httpClient';
import { getOperatorDeviceBindingPayload } from '../../utils/operatorDeviceBinding';

const API_BASE_URL = getApiBaseUrl();

const validationSchema = yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Invalid phone number (10 digits)'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const BusinessAdminOperatorLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { completeLogin, getDefaultRoute } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const magicToken = searchParams.get('token');
    if (!magicToken) return;

    const runMagicLogin = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.post(`${API_BASE_URL}/franchisee/operator/magic-login`, {
          token: magicToken,
          ...(await getOperatorDeviceBindingPayload()),
        });
        if (response.data.success) {
          const { token, user } = response.data;
          completeLogin(token, user, 'admin');
          navigate(getDefaultRoute(user?.role || 'franchisee_operator'), { replace: true });
        } else {
          setError(response.data.message || 'Magic login failed');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Magic login failed');
      } finally {
        setLoading(false);
      }
    };

    runMagicLogin();
  }, [searchParams, completeLogin, navigate]);

  const formik = useFormik({
    initialValues: {
      phone: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');

      try {
        const device = await getOperatorDeviceBindingPayload();
        const response = await axios.post(`${API_BASE_URL}/franchisee/operator/login`, {
          phone: values.phone,
          password: values.password,
          deviceId: device.deviceId,
          platform: device.platform,
        });

        if (response.data.success) {
          const { token, user } = response.data;
          completeLogin(token, user, 'admin');
          navigate(getDefaultRoute(user?.role || 'franchisee_operator'), { replace: true });
        } else {
          setError(response.data.message || 'Login failed');
        }
      } catch (err) {
        setError(
          err.response?.data?.message || 'An error occurred. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Business Operator Login
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to your business operator account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Login'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Operators are created by your business admin
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default BusinessAdminOperatorLogin;
